import type { Ref } from 'vue';

export interface TextFormatValues {
  fontWeight?: 'normal' | 'semibold' | 'bold';
  fontStyle?: 'normal' | 'italic';
  textDecoration?: 'none' | 'underline' | 'line-through';
}

export interface UseTextFormatTogglesParams<T extends TextFormatValues> {
  values: Ref<T | undefined>;
  setValues: (newValues: T) => void;
}

export function useTextFormatToggles<T extends TextFormatValues>({
  values,
  setValues,
}: UseTextFormatTogglesParams<T>) {
  const toggleFontWeight = () => {
    if (!values.value) return;
    const current = values.value.fontWeight || 'normal';
    const next = current === 'bold' ? 'normal' : 'bold';
    setValues({ ...values.value, fontWeight: next });
  };

  const toggleFontStyle = () => {
    if (!values.value) return;
    const current = values.value.fontStyle || 'normal';
    const next = current === 'italic' ? 'normal' : 'italic';
    setValues({ ...values.value, fontStyle: next });
  };

  const toggleUnderline = () => {
    if (!values.value) return;
    const current = values.value.textDecoration || 'none';
    // If underline is already active, turn it off. Otherwise, turn it on (and turn off strikethrough if active)
    const next = current === 'underline' ? 'none' : 'underline';
    setValues({ ...values.value, textDecoration: next });
  };

  const toggleStrikethrough = () => {
    if (!values.value) return;
    const current = values.value.textDecoration || 'none';
    // If strikethrough is already active, turn it off. Otherwise, turn it on (and turn off underline if active)
    const next = current === 'line-through' ? 'none' : 'line-through';
    setValues({ ...values.value, textDecoration: next });
  };

  return {
    toggleFontWeight,
    toggleFontStyle,
    toggleUnderline,
    toggleStrikethrough,
  };
}

